import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  signInAnonymously,
  linkWithCredential,
  EmailAuthProvider,
  sendEmailVerification,
} from "firebase/auth";
import { doc, setDoc, updateDoc, getDoc, collection, query, where, getDocs, serverTimestamp, onSnapshot } from "firebase/firestore";
import { nanoid } from "nanoid";
import { Building2, MapPin, Phone, Camera, UserPlus, Check, ChevronRight, ChevronLeft, ArrowRight, Eye, EyeOff, RefreshCw } from "lucide-react";
import _ from "lodash";
import { auth, db } from "../../firebase.js";
import i18n from "../../i18n.js";
import Logo from "../../components/Logo.js";
import Button from "../../components/Button.js";
import Message from "../../components/Message.js";
import Gallery from "../Venue/components/Gallery.js";
import PhoneUploadCard from "../../components/PhoneUploadCard.js";

const STORAGE_KEY_VENUE = "onboarding-venue-id";
const STORAGE_KEY_STEP = "onboarding-step";

const STEPS = [
  { id: 1, label: "Locatie", icon: Building2 },
  { id: 2, label: "Adres", icon: MapPin },
  { id: 3, label: "Contactgegevens", icon: Phone },
  { id: 4, label: "Foto", icon: Camera },
  { id: 5, label: "Account aanmaken", icon: UserPlus },
];

const PROVINCES = [
  "Drenthe", "Flevoland", "Friesland", "Gelderland", "Groningen",
  "Limburg", "Noord-Brabant", "Noord-Holland", "Overijssel",
  "Utrecht", "Zeeland", "Zuid-Holland",
];

function determineResumeStep(venueData) {
  if (!venueData) return 1;
  const addr = venueData.address || {};
  if (!venueData.name) return 1;
  if (!addr.street || !addr.houseNumber || !addr.postalCode || !addr.city || !addr.province) return 2;
  if (!venueData.email || !venueData.telephone || !venueData.website) return 3;
  return 4;
}

function StepSidebar({ currentStep, className }) {
  return (
    <div className={className}>
      <div className="p-6 mb-2">
        <span className="text-white"><Logo /></span>
      </div>
      <nav className="px-4 space-y-1">
        {STEPS.map((s) => {
          const completed = s.id < currentStep;
          const active = s.id === currentStep;
          return (
            <div
              key={s.id}
              className={[
                "flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-fast",
                active ? "bg-accent-subtle text-accent" : completed ? "text-[rgba(255,255,255,0.8)]" : "text-[rgba(255,255,255,0.35)]",
              ].join(" ")}
            >
              <span
                className={[
                  "w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-all duration-fast",
                  active ? "bg-accent text-white" : completed ? "bg-success text-white" : "bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.4)]",
                ].join(" ")}
              >
                {completed ? <Check size={12} strokeWidth={3} /> : s.id}
              </span>
              <span>{s.label}</span>
            </div>
          );
        })}
      </nav>
      <div className="mt-auto p-6">
        <p className="text-xs text-[rgba(255,255,255,0.3)] leading-relaxed">
          Heb je al een account?{" "}
          <Link to="/login" className="text-[rgba(255,255,255,0.6)] hover:text-white underline underline-offset-2">
            Inloggen
          </Link>
        </p>
      </div>
    </div>
  );
}

function MobileProgress({ currentStep }) {
  return (
    <div className="md:hidden px-6 pt-6 pb-2">
      <div className="flex items-center justify-between mb-3">
        {STEPS.map((s, i) => {
          const completed = s.id < currentStep;
          const active = s.id === currentStep;
          return (
            <React.Fragment key={s.id}>
              <span
                className={[
                  "w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-all duration-fast",
                  active ? "bg-accent text-white" : completed ? "bg-success text-white" : "bg-bg-sunken text-text-tertiary",
                ].join(" ")}
              >
                {completed ? <Check size={12} strokeWidth={3} /> : s.id}
              </span>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 transition-colors duration-fast ${s.id < currentStep ? "bg-success" : "bg-border"}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-text-secondary font-medium">
          Stap {currentStep} van {STEPS.length} — {STEPS[currentStep - 1].label}
        </p>
        <Link to="/login" className="text-xs text-text-tertiary hover:text-accent underline underline-offset-2">
          Inloggen
        </Link>
      </div>
    </div>
  );
}

function Signup() {
  const navigate = useNavigate();

  const [authReady, setAuthReady] = useState(false);
  const [step, setStep] = useState(1);
  const [venueId, setVenueId] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [eventTypes, setEventTypes] = useState([]);
  const [address, setAddress] = useState({ street: "", houseNumber: "", postalCode: "", city: "", province: "" });
  const [contact, setContact] = useState({ website: "", email: "", telephone: "" });
  const [photos, setPhotos] = useState([]);

  const [accountEmail, setAccountEmail] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [accountPasswordConfirm, setAccountPasswordConfirm] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(() => window.innerWidth >= 1280);
  const [previewKey, setPreviewKey] = useState(0);
  const iframeRef = useRef(null);
  const initialPhotosRef = useRef([]);
  // Foto's die de gebruiker lokaal heeft verwijderd; die willen we niet opnieuw
  // toevoegen als de live-listener een oudere serverstatus binnenkrijgt.
  const removedPhotosRef = useRef(new Set());

  useEffect(() => {
    async function initAuth() {
      if (auth.currentUser && !auth.currentUser.isAnonymous) {
        return;
      }

      if (auth.currentUser && auth.currentUser.isAnonymous) {
        await resumeOnboarding();
        setAuthReady(true);
        return;
      }

      try {
        await signInAnonymously(auth);
        const savedVenueId = localStorage.getItem(STORAGE_KEY_VENUE);
        if (savedVenueId) {
          const savedStep = parseInt(localStorage.getItem(STORAGE_KEY_STEP), 10);
          try {
            const snap = await getDoc(doc(db, "locations", savedVenueId));
            if (snap.exists()) {
              hydrateFromVenueData(savedVenueId, snap.data(), savedStep);
            } else {
              setVenueId(savedVenueId);
              setStep(savedStep > 0 && savedStep <= 5 ? savedStep : 1);
            }
          } catch (_fetchErr) {
            setVenueId(savedVenueId);
            setStep(savedStep > 0 && savedStep <= 5 ? savedStep : 1);
          }
        }
      } catch (err) {
        console.error("Anonymous auth failed:", err);
        setError("Er is een fout opgetreden. Probeer de pagina te vernieuwen.");
      }
      setAuthReady(true);
    }

    function hydrateFromVenueData(id, data, savedStep) {
      setVenueId(id);
      setName(data.name || "");
      if (data.address) setAddress({ street: "", houseNumber: "", postalCode: "", city: "", province: "", ...data.address });
      if (data.email || data.telephone || data.website) {
        setContact({ website: data.website || "", email: data.email || "", telephone: data.telephone || "" });
      }
      if (data.photos) {
        setPhotos(data.photos);
        initialPhotosRef.current = data.photos;
      }
      if (data.eventTypes) setEventTypes(data.eventTypes);
      const resumeStep = savedStep > 0 && savedStep <= 5 ? savedStep : determineResumeStep(data);
      setStep(resumeStep);
      localStorage.setItem(STORAGE_KEY_VENUE, id);
      localStorage.setItem(STORAGE_KEY_STEP, String(resumeStep));
    }

    async function resumeOnboarding() {
      const savedVenueId = localStorage.getItem(STORAGE_KEY_VENUE);
      const savedStep = parseInt(localStorage.getItem(STORAGE_KEY_STEP), 10);

      if (savedVenueId) {
        try {
          const snap = await getDoc(doc(db, "locations", savedVenueId));
          if (snap.exists()) {
            hydrateFromVenueData(savedVenueId, snap.data(), savedStep);
            return;
          }
        } catch (err) {
          console.error("Resume fetch failed:", err);
        }
        setVenueId(savedVenueId);
        setStep(savedStep > 0 && savedStep <= 5 ? savedStep : 1);
        return;
      }

      try {
        const q = query(collection(db, "locations"), where("uid", "==", auth.currentUser.uid));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const venueDoc = snapshot.docs[0];
          hydrateFromVenueData(venueDoc.id, venueDoc.data(), 0);
        }
      } catch (err) {
        console.error("Resume query failed:", err);
      }
    }

    initAuth();
  }, []);

  useEffect(() => {
    if (!previewOpen || !iframeRef.current || !venueId) return;
    iframeRef.current.contentWindow?.postMessage(
      {
        type: "venue-update",
        venue: {
          objectID: venueId,
          name,
          address,
          website: contact.website,
          email: contact.email,
          telephone: contact.telephone,
          photos,
          eventTypes,
          published: false,
          types: [],
          features: [],
          accessibility: [],
          description: { nl: "" },
          maxCapacity: 0,
          minCapacity: 0,
        },
      },
      "https://feestjegeven.nl",
    );
  }, [previewOpen, venueId, name, eventTypes, address, contact, photos]);

  // Toont foto's die via de telefoon-uploadlink zijn toegevoegd live in de galerij
  // terwijl de gebruiker op de fotostap staat.
  useEffect(() => {
    if (step !== 4 || !venueId) return undefined;
    const unsub = onSnapshot(doc(db, "locations", venueId), (snap) => {
      if (!snap.exists()) return;
      const serverPhotos = snap.data().photos || [];
      setPhotos((prev) => {
        const additions = serverPhotos.filter((id) => !prev.includes(id) && !removedPhotosRef.current.has(id));
        return additions.length ? [...prev, ...additions] : prev;
      });
    }, (err) => console.error("Foto-listener:", err));
    return () => unsub();
  }, [step, venueId]);

  const persistStep = useCallback((stepNum) => {
    localStorage.setItem(STORAGE_KEY_STEP, String(stepNum));
  }, []);

  if (auth.currentUser && !auth.currentUser.isAnonymous) {
    return <Navigate replace to="/venues" />;
  }

  if (!authReady) {
    return (
      <div className="flex items-center justify-center h-screen bg-bg-base">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-text-secondary">Laden...</p>
        </div>
      </div>
    );
  }

  async function handleStep1() {
    if (!name.trim()) {
      setError("Vul de naam van uw locatie in.");
      return;
    }
    setError("");
    setPending(true);

    try {
      const id = venueId || nanoid(10).toLowerCase();
      if (venueId) {
        await updateDoc(doc(db, "locations", id), { name: name.trim(), eventTypes });
      } else {
        await setDoc(doc(db, "locations", id), {
          published: false,
          uid: auth.currentUser.uid,
          createdAt: serverTimestamp(),
          name: name.trim(),
          eventTypes,
        });
      }
      setVenueId(id);
      localStorage.setItem(STORAGE_KEY_VENUE, id);
      setStep(2);
      persistStep(2);
    } catch (err) {
      console.error(err);
      setError("Er is een fout opgetreden. Probeer het opnieuw.");
    }
    setPending(false);
  }

  async function handleStep2() {
    if (!address.street || !address.houseNumber || !address.postalCode || !address.city || !address.province) {
      setError("Vul alle adresvelden in.");
      return;
    }
    setError("");
    setPending(true);

    try {
      await updateDoc(doc(db, "locations", venueId), { address });
      setStep(3);
      persistStep(3);
    } catch (err) {
      console.error(err);
      setError("Er is een fout opgetreden. Probeer het opnieuw.");
    }
    setPending(false);
  }

  async function handleStep3() {
    if (!contact.website || !contact.email || !contact.telephone) {
      setError("Vul alle contactgegevens in.");
      return;
    }
    setError("");
    setPending(true);

    try {
      await updateDoc(doc(db, "locations", venueId), {
        website: contact.website,
        email: contact.email,
        telephone: contact.telephone,
      });
      setStep(4);
      persistStep(4);
    } catch (err) {
      console.error(err);
      setError("Er is een fout opgetreden. Probeer het opnieuw.");
    }
    setPending(false);
  }

  async function handleStep4() {
    setError("");
    setPending(true);

    try {
      // Behoud foto's die via de telefoon-uploadlink zijn toegevoegd.
      let mergedPhotos = photos;
      try {
        const fresh = await getDoc(doc(db, "locations", venueId));
        const serverPhotos = (fresh.exists() && fresh.data().photos) || [];
        const initial = initialPhotosRef.current || [];
        const phoneAdded = serverPhotos.filter((id) => !initial.includes(id) && !photos.includes(id));
        if (phoneAdded.length) {
          mergedPhotos = [...photos, ...phoneAdded];
          setPhotos(mergedPhotos);
        }
      } catch (_e) {
        // Doorgaan met de lokale foto's.
      }
      initialPhotosRef.current = mergedPhotos;
      await updateDoc(doc(db, "locations", venueId), { photos: mergedPhotos });
      setStep(5);
      persistStep(5);
    } catch (err) {
      console.error(err);
      setError("Er is een fout opgetreden. Probeer het opnieuw.");
    }
    setPending(false);
  }

  async function handleStep5(e) {
    e.preventDefault();
    setError("");

    if (!accountEmail.trim()) {
      setError("Vul uw emailadres in.");
      return;
    }
    if (accountPassword.length < 8) {
      setError("Het wachtwoord moet minimaal 8 tekens zijn.");
      return;
    }
    if (accountPassword !== accountPasswordConfirm) {
      setError("De wachtwoorden komen niet overeen.");
      return;
    }
    if (!agreedToTerms) {
      setError("U moet akkoord gaan met de voorwaarden.");
      return;
    }

    setPending(true);

    try {
      const credential = EmailAuthProvider.credential(accountEmail.trim(), accountPassword);
      await linkWithCredential(auth.currentUser, credential);
      await sendEmailVerification(auth.currentUser);
      localStorage.removeItem(STORAGE_KEY_VENUE);
      localStorage.removeItem(STORAGE_KEY_STEP);
      navigate(`/venues/${venueId}`);
    } catch (err) {
      console.error(err);
      if (err.code === "auth/email-already-in-use") {
        setError("Dit emailadres is al in gebruik. Log in met uw bestaand account.");
      } else if (err.code === "auth/weak-password") {
        setError("Het wachtwoord is te zwak. Kies een sterker wachtwoord.");
      } else if (err.code === "auth/invalid-email") {
        setError("Het emailadres is niet geldig.");
      } else {
        setError("Er is een fout opgetreden. Probeer het opnieuw.");
      }
    }
    setPending(false);
  }

  function goBack() {
    setError("");
    const prev = step - 1;
    setStep(prev);
    persistStep(prev);
  }

  function renderStepContent() {
    switch (step) {
      case 1:
        return (
          <div>
            <h2 className="text-2xl font-heading font-bold mb-2">Hoe heet uw locatie?</h2>
            <p className="text-sm text-text-secondary mb-6">
              Dit is de naam die bezoekers zien op uw pagina. U kunt dit later altijd wijzigen.
            </p>

            <div className="card p-5 mb-6 space-y-2">
              <p className="text-sm font-medium text-text-primary mb-2">Waarom adverteren op Feestjegeven?</p>
              {[
                "Wij zijn één van de grootste spelers met het mooiste aanbod",
                "Adverteren is GRATIS",
                "Aanvragen worden direct doorgestuurd",
                "Bij ons staat gebruikservaring voorop",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Check size={14} strokeWidth={2} className="text-success shrink-0 mt-0.5" />
                  <span className="text-sm text-text-secondary">{item}</span>
                </div>
              ))}
            </div>

            <label className="label-base">Naam van de locatie <span className="text-error">*</span></label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="bijv. Het Strandpaviljoen"
              className="input-base mb-6"
              autoFocus
            />

            <label className="label-base mb-2 block">Geschikt voor</label>
            <p className="text-xs text-text-tertiary mb-3">Selecteer voor welke gelegenheden uw locatie geschikt is.</p>
            <div className="grid grid-cols-2 gap-2 mb-6">
              {_.map(i18n.eventTypes, (slug, label) => (
                <label
                  key={slug}
                  className={[
                    "flex items-center gap-2.5 px-3 py-2.5 rounded-sm cursor-pointer border transition-all duration-fast text-sm",
                    _.includes(eventTypes, slug)
                      ? "bg-accent-subtle border-accent/30 text-accent font-medium"
                      : "bg-bg-surface border-border text-text-secondary hover:border-border-hover hover:bg-bg-sunken",
                  ].join(" ")}
                >
                  <input
                    type="checkbox"
                    checked={_.includes(eventTypes, slug)}
                    onChange={() => setEventTypes((prev) => _.xor(prev, [slug]))}
                    className="sr-only"
                  />
                  {label}
                </label>
              ))}
            </div>

            {error && <div className="mb-4"><Message text={error} /></div>}

            <Button label="Volgende" onClick={handleStep1} pending={pending} loadingLabel="Een momentje..." icon={ChevronRight} className="w-full sm:w-auto" />
          </div>
        );

      case 2:
        return (
          <div>
            <h2 className="text-2xl font-heading font-bold mb-2">Waar is uw locatie?</h2>
            <p className="text-sm text-text-secondary mb-6">Vul het adres in zodat bezoekers u kunnen vinden.</p>

            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-4">
                <div>
                  <label className="label-base">Straat <span className="text-error">*</span></label>
                  <input type="text" value={address.street} onChange={(e) => setAddress({ ...address, street: e.target.value })} className="input-base" />
                </div>
                <div>
                  <label className="label-base">Nr. <span className="text-error">*</span></label>
                  <input type="text" value={address.houseNumber} onChange={(e) => setAddress({ ...address, houseNumber: e.target.value })} className="input-base" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-4">
                <div>
                  <label className="label-base">Postcode <span className="text-error">*</span></label>
                  <input type="text" value={address.postalCode} onChange={(e) => setAddress({ ...address, postalCode: e.target.value })} className="input-base" />
                </div>
                <div>
                  <label className="label-base">Plaats <span className="text-error">*</span></label>
                  <input type="text" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} className="input-base" />
                </div>
              </div>
              <div>
                <label className="label-base">Provincie <span className="text-error">*</span></label>
                <select value={address.province} onChange={(e) => setAddress({ ...address, province: e.target.value })} className="input-base appearance-none cursor-pointer">
                  <option value="">--- Kies provincie ---</option>
                  {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>

            {error && <div className="mb-4"><Message text={error} /></div>}

            <div className="flex gap-3">
              <Button variant="secondary" label="Terug" onClick={goBack} icon={ChevronLeft} />
              <Button label="Volgende" onClick={handleStep2} pending={pending} loadingLabel="Een momentje..." icon={ChevronRight} />
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <h2 className="text-2xl font-heading font-bold mb-2">Contactgegevens van uw locatie</h2>
            <p className="text-sm text-text-secondary mb-6">
              Deze gegevens worden getoond op uw locatiepagina zodat bezoekers u kunnen bereiken.
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="label-base">Website <span className="text-error">*</span></label>
                <input type="text" value={contact.website} onChange={(e) => setContact({ ...contact, website: e.target.value })} placeholder="https://..." className="input-base" />
              </div>
              <div>
                <label className="label-base">Contactemail <span className="text-error">*</span></label>
                <input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="info@uwlocatie.nl" className="input-base" />
                <p className="text-xs text-text-tertiary mt-1.5">Dit is het emailadres dat bezoekers te zien krijgen.</p>
              </div>
              <div>
                <label className="label-base">Telefoon <span className="text-error">*</span></label>
                <input type="tel" value={contact.telephone} onChange={(e) => setContact({ ...contact, telephone: e.target.value })} placeholder="bijv. 020 1234567" className="input-base" />
              </div>
            </div>

            {error && <div className="mb-4"><Message text={error} /></div>}

            <div className="flex gap-3">
              <Button variant="secondary" label="Terug" onClick={goBack} icon={ChevronLeft} />
              <Button label="Volgende" onClick={handleStep3} pending={pending} loadingLabel="Een momentje..." icon={ChevronRight} />
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <h2 className="text-2xl font-heading font-bold mb-2">Voeg een foto toe</h2>
            <p className="text-sm text-text-secondary mb-6">
              Upload minimaal 1 foto van uw locatie (minimaal 1600x800 pixels). U kunt later meer foto's toevoegen.
            </p>

            {venueId && (
              <div className="mb-6">
                <PhoneUploadCard venueId={venueId} venueName={name} />
              </div>
            )}

            {venueId && (
              <div className="mb-6">
                <Gallery
                  objectID={venueId}
                  images={photos}
                  hasPremiumAccount={false}
                  onImageRemove={(id) => {
                    removedPhotosRef.current.add(id);
                    setPhotos((prev) => prev.filter((p) => p !== id));
                  }}
                  onImageAdd={(id) => {
                    removedPhotosRef.current.delete(id);
                    setPhotos((prev) => [...prev, id]);
                  }}
                  onSortEnd={({ active, over }) => {
                    if (over && active.id !== over.id) {
                      const oldIndex = photos.indexOf(active.id);
                      const newIndex = photos.indexOf(over.id);
                      const newPhotos = [...photos];
                      newPhotos.splice(oldIndex, 1);
                      newPhotos.splice(newIndex, 0, active.id);
                      setPhotos(newPhotos);
                    }
                  }}
                />
              </div>
            )}

            {photos.length === 0 && (
              <Message variant="warning" text="Wij hebben tenminste 1 foto nodig om uw locatie online te kunnen plaatsen. U kunt dit ook later doen." />
            )}

            {error && <div className="mt-4"><Message text={error} /></div>}

            <div className="flex gap-3 mt-6">
              <Button variant="secondary" label="Terug" onClick={goBack} icon={ChevronLeft} />
              {photos.length === 0 ? (
                <Button variant="secondary" label="Overslaan" onClick={handleStep4} pending={pending} loadingLabel="Een momentje..." icon={ChevronRight} />
              ) : (
                <Button label="Volgende" onClick={handleStep4} pending={pending} loadingLabel="Een momentje..." icon={ChevronRight} />
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <form onSubmit={handleStep5}>
            <h2 className="text-2xl font-heading font-bold mb-2">Maak uw account aan</h2>
            <p className="text-sm text-text-secondary mb-6">
              Nog even een account aanmaken om uw locatie te beheren. Na registratie ontvangt u een bevestigingsemail.
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="label-base">Uw login-emailadres <span className="text-error">*</span></label>
                <input
                  type="email"
                  value={accountEmail}
                  onChange={(e) => setAccountEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="uw@email.nl"
                  required
                  className="input-base"
                />
                <p className="text-xs text-text-tertiary mt-1.5">Dit is het emailadres waarmee u inlogt. Dit kan anders zijn dan het contactemail van uw locatie.</p>
              </div>
              <div>
                <label className="label-base">Wachtwoord <span className="text-error">*</span></label>
                <input
                  type="password"
                  value={accountPassword}
                  onChange={(e) => setAccountPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength="8"
                  placeholder="Minimaal 8 tekens"
                  required
                  className="input-base"
                />
              </div>
              <div>
                <label className="label-base">Wachtwoord bevestigen <span className="text-error">*</span></label>
                <input
                  type="password"
                  value={accountPasswordConfirm}
                  onChange={(e) => setAccountPasswordConfirm(e.target.value)}
                  autoComplete="new-password"
                  minLength="8"
                  placeholder="Herhaal uw wachtwoord"
                  required
                  className="input-base"
                />
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 accent-accent"
                />
                <span className="text-sm text-text-secondary">
                  Ik ga akkoord met de{" "}
                  <a
                    href="/static/GEBRUIKSOVEREENKOMST_FG.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline underline-offset-2"
                  >
                    voorwaarden
                  </a>{" "}
                  van Feestjegeven.nl
                </span>
              </label>
            </div>

            {error && (
              <div className="mb-4">
                <Message>
                  <div>
                    {error}
                    {error.includes("al in gebruik") && (
                      <Link to="/login" className="block mt-2 text-sm text-accent font-medium hover:underline underline-offset-2">
                        Ga naar inloggen
                      </Link>
                    )}
                  </div>
                </Message>
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="secondary" label="Terug" type="button" onClick={goBack} icon={ChevronLeft} />
              <Button type="submit" label="Account aanmaken" pending={pending} loadingLabel="Even geduld..." icon={ArrowRight} />
            </div>
          </form>
        );

      default:
        return null;
    }
  }

  return (
    <div className="min-h-screen bg-bg-base flex flex-col md:flex-row">
      <StepSidebar
        currentStep={step}
        className="hidden md:flex md:flex-col md:w-sidebar md:fixed md:inset-y-0 md:left-0 bg-bg-sidebar"
      />

      <div className={`flex-1 md:ml-sidebar transition-[padding] duration-slow ease-snappy ${previewOpen && venueId ? "xl:pr-[544px]" : ""}`}>
        <MobileProgress currentStep={step} />

        <div className="flex items-start justify-center p-6 sm:p-8 md:p-12 min-h-[calc(100vh-80px)] md:min-h-screen">
          <div className="w-full max-w-lg animate-fade-in">
            {renderStepContent()}
          </div>
        </div>
      </div>

      {/* Preview drawer with attached tab — visible once the venue doc exists */}
      {venueId && (
        <div
          className="hidden xl:block fixed top-0 right-0 bottom-0 z-30"
          style={{
            width: "min(520px, 40vw)",
            transform: previewOpen ? "translateX(0)" : "translateX(100%)",
            transition: "transform 300ms cubic-bezier(0.2, 0, 0, 1)",
          }}
        >
          <button
            type="button"
            onClick={() => setPreviewOpen((o) => !o)}
            className={[
              "absolute z-10 flex flex-col items-center gap-2 rounded-l-lg border border-r-0 shadow-md cursor-pointer transition-colors duration-fast",
              previewOpen
                ? "bg-accent border-accent/80 text-white"
                : "bg-bg-surface border-border text-text-secondary hover:text-text-primary hover:bg-bg-sunken",
            ].join(" ")}
            style={{ right: "100%", top: "72px", padding: "12px 7px" }}
          >
            {previewOpen ? <EyeOff size={14} /> : <Eye size={14} />}
            <span
              className="text-[10px] font-semibold tracking-widest uppercase leading-none"
              style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
            >
              Voorbeeld
            </span>
          </button>

          <div className="h-full flex flex-col bg-bg-surface border-l border-border shadow-xl">
            <div className="flex items-center justify-between gap-3 px-4 h-topbar border-b border-border shrink-0">
              <span className="text-sm font-medium text-text-primary truncate">
                {name || "Voorbeeld"}
              </span>
              <button
                type="button"
                title="Vernieuwen"
                onClick={() => setPreviewKey((k) => k + 1)}
                className="p-1.5 rounded-sm text-text-tertiary hover:text-text-primary hover:bg-bg-sunken transition-colors duration-fast"
              >
                <RefreshCw size={13} />
              </button>
            </div>

            <iframe
              ref={iframeRef}
              key={previewKey}
              src={`https://feestjegeven.nl/l/embed/${venueId}`}
              title="Locatievoorbeeld"
              className="flex-1 w-full border-0 bg-bg-base"
              loading="eager"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Signup;
